/**
 * F39 (Wave 2C) — admin "edit placement inventory" modal.
 *
 * Composes Modal + CurrencyInput (base price, USD minor units) + NumberInput
 * (scarcity cap / lead hours / advance days) + Select (tier restriction) +
 * Switch (active) + Textarea (mandatory reason ≥10) + a forward-only Banner.
 *
 * If-Match optimistic concurrency: the dialog sends `config.updatedAt` so a
 * stale edit returns 409 PLACEMENT_INVENTORY_STALE (surfaced inline). Gated
 * server-side on `placement_inventory:write`; the catalogue page hides the CTA
 * without it.
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  CurrencyInput,
  FormField,
  Modal,
  NumberInput,
  Select,
  Switch,
  Textarea,
} from '@bravemove/ui-web';
import { EditPlacementInventoryInputSchema } from '@bravemove/types';
import { adminPlacementsApi } from '../../api/placements';
import type { PlacementInventoryConfig } from '@bravemove/types';

export interface EditInventoryDialogProps {
  readonly open: boolean;
  readonly config: PlacementInventoryConfig;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}

type TierRestrictionSelect = 'none' | 'pro_plus';

export function EditInventoryDialog({
  open,
  config,
  onConfirm,
  onClose,
}: EditInventoryDialogProps) {
  const { t } = useI18n();
  const [basePrice, setBasePrice] = useState<number>(config.basePriceMinor);
  const [uncapped, setUncapped] = useState<boolean>(config.scarcityCap === null);
  const [capValue, setCapValue] = useState<number>(config.scarcityCap ?? 1);
  const [minLeadHours, setMinLeadHours] = useState<number>(config.minLeadHours);
  const [maxAdvanceDays, setMaxAdvanceDays] = useState<number>(config.maxAdvanceDays);
  const [tierRestriction, setTierRestriction] = useState<TierRestrictionSelect>(
    config.tierRestriction === 'pro_plus' ? 'pro_plus' : 'none',
  );
  const [active, setActive] = useState<boolean>(config.active);
  const [reason, setReason] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    /* eslint-disable react-hooks/set-state-in-effect */
    setBasePrice(config.basePriceMinor);
    setUncapped(config.scarcityCap === null);
    setCapValue(config.scarcityCap ?? 1);
    setMinLeadHours(config.minLeadHours);
    setMaxAdvanceDays(config.maxAdvanceDays);
    setTierRestriction(config.tierRestriction === 'pro_plus' ? 'pro_plus' : 'none');
    setActive(config.active);
    setReason('');
    setServerError(null);
    setSubmitting(false);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [
    open,
    config.basePriceMinor,
    config.scarcityCap,
    config.minLeadHours,
    config.maxAdvanceDays,
    config.tierRestriction,
    config.active,
  ]);

  const reasonTrimmed = reason.trim();
  const reasonValid = reasonTrimmed.length >= 10 && reasonTrimmed.length <= 500;
  const disableSubmit = submitting || !reasonValid;

  const submit = async () => {
    setServerError(null);
    const candidate = {
      basePriceMinor: basePrice,
      scarcityCap: uncapped ? null : capValue,
      minLeadHours,
      maxAdvanceDays,
      tierRestriction: tierRestriction === 'pro_plus' ? ('pro_plus' as const) : null,
      active,
      reason: reasonTrimmed,
    };
    const parsed = EditPlacementInventoryInputSchema.safeParse(candidate);
    if (!parsed.success) {
      setServerError(
        t('placement:admin.inventory.error_invalid', {
          defaultValue: 'Check the price, caps, and reason (10–500 chars).',
        }),
      );
      return;
    }
    setSubmitting(true);
    try {
      await adminPlacementsApi.editInventory(config.placementType, parsed.data, config.updatedAt);
      onConfirm();
      onClose();
    } catch (err) {
      const code =
        err !== null && typeof err === 'object' && 'code' in err
          ? (err as { code?: string }).code
          : null;
      if (code === 'PLACEMENT_INVENTORY_STALE') {
        setServerError(
          t('placement:admin.inventory.error_stale', {
            defaultValue:
              'This placement type changed since you opened the editor. Reload and try again.',
          }),
        );
      } else {
        setServerError(
          t('placement:admin.inventory.error_generic', {
            defaultValue: 'Something went wrong. Please try again.',
          }),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('placement:admin.inventory.edit_title', {
        type: config.displayName,
        defaultValue: 'Edit inventory — {type}',
      })}
      description={t('placement:admin.inventory.edit_description', {
        defaultValue: 'Pricing + caps apply forward-only. Existing placements keep their snapshot.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="edit-inventory-cancel"
          >
            {t('common:cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={disableSubmit}
            data-testid="edit-inventory-confirm"
          >
            {t('common:save', { defaultValue: 'Save' })}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        data-testid="edit-inventory-dialog"
        data-placement-type={config.placementType}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div data-testid="edit-inventory-forward-only-banner">
          <Banner
            tone="info"
            title={t('placement:admin.inventory.banner_title', {
              defaultValue: 'Forward-only change',
            })}
          >
            {t('placement:admin.inventory.banner_body', {
              defaultValue:
                'Existing placements keep their snapshotted price. New purchases use the new values.',
            })}
          </Banner>
        </div>

        <FormField
          label={t('placement:admin.inventory.base_price', {
            defaultValue: 'Base price (per week)',
          })}
        >
          <CurrencyInput
            value={basePrice}
            currency={config.priceCurrency}
            ariaLabel={t('placement:admin.inventory.base_price', {
              defaultValue: 'Base price (per week)',
            })}
            onChange={(v) => setBasePrice(v)}
          />
        </FormField>

        <FormField
          label={t('placement:admin.inventory.scarcity_cap', { defaultValue: 'Scarcity cap' })}
          helper={t('placement:admin.inventory.scarcity_helper', {
            defaultValue:
              'Max simultaneous placements per scope-week. Toggle uncapped to remove the limit.',
          })}
        >
          <div className="flex flex-col gap-2">
            <Switch
              checked={uncapped}
              data-testid="edit-inventory-uncapped"
              onChange={(e) => setUncapped(e.target.checked)}
              label={t('placement:admin.inventory.uncapped', { defaultValue: 'Uncapped' })}
            />
            {!uncapped ? (
              <NumberInput
                value={capValue}
                min={1}
                data-testid="edit-inventory-scarcity-cap"
                onChange={(v) => setCapValue(Number(v))}
              />
            ) : null}
          </div>
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            label={t('placement:admin.inventory.min_lead_hours', {
              defaultValue: 'Minimum lead time (hours)',
            })}
          >
            <NumberInput
              value={minLeadHours}
              min={0}
              data-testid="edit-inventory-lead-hours"
              onChange={(v) => setMinLeadHours(Number(v))}
            />
          </FormField>
          <FormField
            label={t('placement:admin.inventory.max_advance_days', {
              defaultValue: 'Max advance (days)',
            })}
          >
            <NumberInput
              value={maxAdvanceDays}
              min={1}
              data-testid="edit-inventory-advance-days"
              onChange={(v) => setMaxAdvanceDays(Number(v))}
            />
          </FormField>
        </div>

        <FormField
          label={t('placement:admin.inventory.tier_restriction', {
            defaultValue: 'Tier restriction',
          })}
        >
          <Select
            value={tierRestriction}
            data-testid="edit-inventory-tier-restriction"
            aria-label={t('placement:admin.inventory.tier_restriction', {
              defaultValue: 'Tier restriction',
            })}
            options={[
              {
                value: 'none',
                label: t('placement:admin.inventory.no_restriction', { defaultValue: 'All tiers' }),
              },
              {
                value: 'pro_plus',
                label: t('placement:tier_restriction.pro_plus', {
                  defaultValue: 'Pro plan and above',
                }),
              },
            ]}
            onChange={(e) => setTierRestriction(e.target.value as TierRestrictionSelect)}
          />
        </FormField>

        <FormField label={t('placement:admin.inventory.active', { defaultValue: 'Active' })}>
          <Switch
            checked={active}
            data-testid="edit-inventory-active"
            onChange={(e) => setActive(e.target.checked)}
            label={t('placement:admin.inventory.active_label', {
              defaultValue: 'Available for purchase',
            })}
          />
        </FormField>

        <FormField
          label={t('placement:admin.inventory.reason', { defaultValue: 'Reason (audit-logged)' })}
          required
        >
          <Textarea
            rows={3}
            maxLength={500}
            value={reason}
            data-testid="edit-inventory-reason"
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>

        {serverError ? (
          <div data-testid="edit-inventory-server-error">
            <Banner tone="danger">{serverError}</Banner>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

export default EditInventoryDialog;
